import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SeasonType } from '@/model';
import { GroupStageItem, KnockoutStageItem, SeasonFormData } from '../schemas';
import {
  DEFAULT_HYBRID_KNOCKOUT_STAGE_KEYS,
  DEFAULT_KNOCKOUT_STAGE_KEYS,
} from '../constants';
import { groupLetter } from '../utils/groupName';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const buildKnockoutDefaults = (
  keys: readonly string[],
  tr: Translate,
): KnockoutStageItem[] =>
  keys.map(key => ({
    name: tr(`pages.application_season.components.season_step2.default_stage_names.${key}`),
  }));

const buildGroupStages = (
  count: number,
  tr: Translate,
  current: GroupStageItem[] = [],
): GroupStageItem[] =>
  Array.from({ length: Math.max(count, 0) }, (_, index) => {
    const existing = current[index];
    const base: GroupStageItem = {
      group_name:
        existing && existing.group_name.trim()
          ? existing.group_name
          : tr('pages.application_season.components.season_step2.group_default_name', {
              letter: groupLetter(index),
            }),
      team_uuids: existing?.team_uuids ?? [],
      direct_qualifiers_count: existing?.direct_qualifiers_count ?? 0,
      playoff_qualifiers_count: existing?.playoff_qualifiers_count ?? 0,
      secondary_qualifiers_count: existing?.secondary_qualifiers_count ?? 0,
      relegated_count: existing?.relegated_count ?? 0,
    };
    if (existing?.uuid) base.uuid = existing.uuid;
    return base;
  });

/**
 * Semeia/regenera as fases do formulário conforme o formato escolhido na etapa 1:
 * - LEAGUE   → nenhuma fase editável (fase única é gerada no submit)
 * - KNOCKOUT → sugestões de mata-mata
 * - HYBRID   → N fases de grupo (de `groups_count`) + sugestões eliminatórias
 *
 * Ao alterar `groups_count` em uma temporada HYBRID, redimensiona as fases de
 * grupo preservando os nomes já editados pelo usuário.
 *
 * A primeira execução é ignorada para não sobrescrever dados carregados (edição).
 */
export const useStageSeeding = (
  form: UseFormReturn<SeasonFormData>,
  enabled = true,
) => {
  const { t } = useTranslation();
  const seasonType = form.watch('season_type');
  const groupsCount = form.watch('groups_count');

  const mounted = useRef(false);
  const prevType = useRef(seasonType);
  const prevGroups = useRef(groupsCount);

  useEffect(() => {
    if (!enabled) return;
    const tr: Translate = (key, opts) => String(t(key, opts));

    if (!mounted.current) {
      mounted.current = true;
      prevType.current = seasonType;
      prevGroups.current = groupsCount;
      return;
    }

    const typeChanged = seasonType !== prevType.current;
    const groupsChanged = groupsCount !== prevGroups.current;
    prevType.current = seasonType;
    prevGroups.current = groupsCount;

    if (typeChanged) {
      if (seasonType === SeasonType.LEAGUE) {
        form.setValue('group_stages', []);
        form.setValue('knockout_stages', []);
      } else if (seasonType === SeasonType.KNOCKOUT) {
        form.setValue('group_stages', []);
        form.setValue('knockout_stages', buildKnockoutDefaults(DEFAULT_KNOCKOUT_STAGE_KEYS, tr));
      } else if (seasonType === SeasonType.HYBRID) {
        form.setValue('group_stages', buildGroupStages(groupsCount, tr));
        form.setValue(
          'knockout_stages',
          buildKnockoutDefaults(DEFAULT_HYBRID_KNOCKOUT_STAGE_KEYS, tr),
        );
      }
      return;
    }

    if (groupsChanged && seasonType === SeasonType.HYBRID) {
      const current = form.getValues('group_stages');
      form.setValue('group_stages', buildGroupStages(groupsCount, tr, current));
    }
  }, [enabled, seasonType, groupsCount, form, t]);
};
